import { useState } from 'react'
import { toast } from 'sonner'

import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { OrgBusiness } from '../../models/business'
import { BusinessDetailsFields } from './BusinessDetailsFields'
import { useBusinessDetailsForm } from './useBusinessDetailsForm'

interface EditBusinessDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  business: OrgBusiness | null | undefined
  entityType?: string | null
  hasEld?: boolean | null
  eldProvider?: string | null
  coverageLapseDetected?: boolean
  cancellationReason?: string | null
  orgId: string
}

export function EditBusinessDetailsDialog({
  open,
  onOpenChange,
  onSuccess,
  business,
  entityType,
  hasEld,
  eldProvider,
  coverageLapseDetected,
  cancellationReason,
  orgId,
}: EditBusinessDetailsDialogProps) {
  const [loading, setLoading] = useState(false)
  const requote = useRequoteOnSave()
  const editor = useBusinessDetailsForm({
    business,
    entityType,
    hasEld,
    eldProvider,
    coverageLapseDetected,
    cancellationReason,
  })

  function close() {
    editor.discard()
    onOpenChange(false)
  }

  async function onSubmit() {
    setLoading(true)
    try {
      const result = await editor.save(orgId, { onlyChanged: false })
      if (!result.ok) {
        if (result.error) toast.error('Failed to update business details')
        return
      }
      onSuccess()
      if (requote) await requote.run()
      else toast.success('Business details updated')
      onOpenChange(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : close())}>
      <DialogContent>
        <form onSubmit={editor.form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit business details</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <BusinessDetailsFields form={editor.form} coverageLapseDetected={coverageLapseDetected} />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              loading={loading ? (requote?.pendingLabel ?? 'Saving...') : undefined}
            >
              {requote?.label ?? 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
